import React, { memo, useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import FormGroup from '../../bootstrap/forms/FormGroup';
import Input from '../../bootstrap/forms/Input';
import Checks from '../../bootstrap/forms/Checks';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import Button from '../../bootstrap/Button';
import Spinner from '../../bootstrap/Spinner';
import PreAlert from '../../../helpers/utils/preAlert';
import masks, { unmask } from '../../../helpers/utils/masks';
import { DefaultContext } from '../../../contexts/default';
import DiscountGroupDB from '../../../database/wrappers/discountGroup';

const validate = (values) => {
  const errors = {};

  if (!values.name) {
    errors.name = 'Este campo é necessário.';
  }

  if (!values.discountValue && values.discountValue !== 0) {
    errors.discountValue = 'Este campo é necessário.';
  }

  return errors;
};

const ModalRegisterDiscount = ({ open, setIsOpen, setIsClose, discount }) => {
  const { estabSelected, storeSelected, onShowAlert } = useContext(DefaultContext);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: '',
      discountValue: '',
      active: true,
      isValue: false,
    },
    validate,
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        discountValue: values.isValue
          ? Number(unmask(String(values.discountValue)))
          : Number(values.discountValue),
        active: values.active,
        isValue: values.isValue,
      };

      const discountDB = new DiscountGroupDB(estabSelected, storeSelected);

      try {
        setLoading(true);

        if (discount) {
          await discountDB.update(discount.id, payload);
          onShowAlert(PreAlert.success('Desconto atualizado com sucesso'));
        } else {
          await discountDB.create(payload);
          onShowAlert(PreAlert.success('Desconto cadastrado com sucesso'));
        }

        setIsClose();
      } catch (error) {
        console.error(error);
        onShowAlert(PreAlert.error('Falhou ao salvar o desconto'));
      } finally {
        setLoading(false);
      }
    },
  });

  const { resetForm, setValues } = formik;

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }

    if (discount) {
      setValues({
        name: discount.name || '',
        discountValue: discount.discountValue ?? '',
        active: discount.active ?? true,
        isValue: discount.isValue ?? false,
      });
    }
  }, [discount, open, resetForm, setValues]);

  return (
    <Modal
      id='modal-register-discount'
      titleId='modal-register-discount-title'
      isOpen={open}
      setIsOpen={setIsOpen}
      isStaticBackdrop
      isCentered
      size='sm'
      isAnimation
    >
      <ModalHeader setIsOpen={setIsOpen}>
        <ModalTitle id='modal-register-discount-title'>
          {discount ? 'Atualização de Desconto' : 'Cadastro de Desconto'}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className='row g-4'>
            <FormGroup id='name' label='Nome' className='col-12'>
              <Input
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                isValid={formik.isValid}
                isTouched={formik.touched.name}
                invalidFeedback={formik.errors.name}
                validFeedback='Assim está bom!'
                placeholder='Nome do desconto'
              />
            </FormGroup>

            <FormGroup id='isValue' label='Percentual ou valor?' className='col-12'>
              <Checks
                type='switch'
                id='isValue'
                label={formik.values.isValue ? 'Valor' : 'Percentual'}
                name='isValue'
                onChange={formik.handleChange}
                checked={formik.values.isValue}
              />
            </FormGroup>

            <FormGroup
              id='discountValue'
              label={`Valor do desconto ${formik.values.isValue ? '(R$)' : '(%)'}`}
              className='col-12'>
              <Input
                type={formik.values.isValue ? 'text' : 'number'}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.isValue
                  ? masks.money(String(formik.values.discountValue || ''))
                  : formik.values.discountValue}
                isValid={formik.isValid}
                isTouched={formik.touched.discountValue}
                invalidFeedback={formik.errors.discountValue}
                validFeedback='Assim está bom!'
                placeholder={formik.values.isValue ? 'Ex: 10,00' : 'Ex: 10'}
              />
            </FormGroup>

            <FormGroup id='active' label='Status' className='col-12'>
              <Checks
                type='switch'
                id='active'
                label={formik.values.active ? 'Ativo' : 'Inativo'}
                name='active'
                onChange={formik.handleChange}
                checked={formik.values.active}
              />
            </FormGroup>
          </div>

          <div className='row pt-4'>
            <div className='d-flex justify-content-evenly flex-row-reverse'>
              <Button
                type='submit'
                color={loading ? 'success' : 'primary'}
                icon={loading ? undefined : 'Add'}
                shadow='sm'
                hoverShadow='sm'
                isDisable={loading || !formik.isValid}>
                {loading ? (
                  <>
                    <Spinner
                      color='light'
                      inButton
                      isGrow
                      isSmall
                      size={10}
                      tag='span'
                    />
                    Carregando...
                  </>
                ) : discount ? 'Atualizar' : 'Adicionar'}
              </Button>

              <Button
                color='danger'
                icon='Cancel'
                rounded={1}
                onClick={setIsClose}
                shadow='sm'
                hoverShadow='sm'
                size='sm'>
                Cancelar
              </Button>
            </div>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default memo(ModalRegisterDiscount);
